import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Calendar as CalendarIcon,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  Bell,
  ClipboardList,
  Stethoscope,
  ChevronRight,
  Check,
  Mail,
  Phone,
  Smile,
  Building2,
  Zap,
  QrCode,
  Receipt,
  MailWarning,
  X as CloseIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import moment from 'moment';
import { apiService, Appointment, GoogleCalendarEvent, Provider, Procedure, Operatory, CommunicationFailedCount } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import StatusPill, { StatusTone } from './ui/StatusPill';
import { formatDate as fmtLocaleDate, formatDateTime as fmtLocaleDateTime } from '../../i18n/locale';

interface DashboardStats {
  todaysAppointments: number;
  todaysGoogleOnly: number; // subset of todaysAppointments that are Google-only events
  completedAppointments: number;
  completedGoogleOnly: number;
  pendingConfirmations: number;
  noShows: number;
}

type UpcomingItem =
  | { kind: 'appt'; sortKey: string; appt: Appointment }
  | { kind: 'google'; sortKey: string; gevent: GoogleCalendarEvent; date: string; time: string };

// Basis-URL der öffentlichen Site für den Intake-Link (analog AppointmentModal).
const PUBLIC_SITE_BASE = process.env.NODE_ENV === 'production'
  ? 'https://seadent.es'
  : 'http://localhost:3001';

const getGreetingKey = () => {
  const h = new Date().getHours();
  if (h < 11) return 'dashboard.greetingMorning';
  if (h < 18) return 'dashboard.greetingDay';
  return 'dashboard.greetingEvening';
};

const STATUS_META: Record<string, { labelKey: string; tone: StatusTone }> = {
  confirmed: { labelKey: 'dashboard.statusConfirmed', tone: 'success' },
  pending: { labelKey: 'dashboard.statusPending', tone: 'warning' },
  scheduled: { labelKey: 'dashboard.statusScheduled', tone: 'info' },
  completed: { labelKey: 'dashboard.statusCompleted', tone: 'info' },
  cancelled: { labelKey: 'dashboard.statusCancelled', tone: 'danger' },
  no_show: { labelKey: 'dashboard.statusNoShow', tone: 'neutral' },
};

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: number;
  tone: 'primary' | 'success' | 'warning' | 'danger';
  subtitle?: string;
}

const TONE_CHIP: Record<StatCardProps['tone'], string> = {
  primary: 'bg-brand-accent/15 text-brand-primary',
  success: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400',
  warning: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
  danger: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400',
};

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, tone, subtitle }) => (
  <div className="rounded-xl bg-brand-surface border border-brand-border p-3 sm:p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="flex items-start justify-between gap-2 sm:gap-3">
      <div className="min-w-0">
        <p className="text-[10px] sm:text-[11px] font-medium uppercase tracking-wider text-brand-text-muted leading-tight">
          {label}
        </p>
        <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-brand-text leading-none tabular-nums">
          {value}
        </p>
        {subtitle && (
          <p className="mt-1 text-[10px] sm:text-[11px] text-brand-text-muted leading-tight">{subtitle}</p>
        )}
      </div>
      <div className={`flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-lg ${TONE_CHIP[tone]}`}>
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.75} aria-hidden />
      </div>
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, can } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    todaysAppointments: 0,
    todaysGoogleOnly: 0,
    completedAppointments: 0,
    completedGoogleOnly: 0,
    pendingConfirmations: 0,
    noShows: 0,
  });
  const [pendingWebsite, setPendingWebsite] = useState<Appointment[]>([]);
  const [googleOnlyCount, setGoogleOnlyCount] = useState(0);
  const [googleOnlyEvents, setGoogleOnlyEvents] = useState<GoogleCalendarEvent[]>([]);
  const [showGoogleList, setShowGoogleList] = useState(false);
  const [upcomingMerged, setUpcomingMerged] = useState<UpcomingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Termin, dessen Anamnese-QR gerade im Datenschutz-Overlay gezeigt wird.
  const [qrAppointment, setQrAppointment] = useState<Appointment | null>(null);

  // QR-Overlay: ESC schließt, Hintergrund-Scroll gesperrt solange offen.
  useEffect(() => {
    if (!qrAppointment) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setQrAppointment(null); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [qrAppointment]);

  // Self-Service-Aktionen von Patienten (Absagen/Umbuchungen, letzte 7 Tage)
  interface PatientAction {
    id: number;
    appointment_id: number;
    action: 'cancelled' | 'rescheduled';
    changed_at: string;
    acknowledged_at: string | null;
    acknowledged_by: string | null;
    patient_name: string;
    procedure_name: string;
    appointment_date: string | null;
    start_time: string | null;
    previous_date: string | null;
    previous_time: string | null;
  }
  const [patientActions, setPatientActions] = useState<PatientAction[]>([]);
  // Zeigt erledigte (quittierte) Aktionen wieder an — "Event Log + Toggle".
  const [showAckedActions, setShowAckedActions] = useState(false);

  // Mini-CRM: fehlgeschlagene Mail-Sends der letzten 7 Tage (rose Badge).
  const [failedMail, setFailedMail] = useState<CommunicationFailedCount | null>(null);

  // Bulk-import modal state
  const [showImport, setShowImport] = useState(false);
  const [importProviders, setImportProviders] = useState<Provider[]>([]);
  const [importProcedures, setImportProcedures] = useState<Procedure[]>([]);
  const [importOperatories, setImportOperatories] = useState<Operatory[]>([]);
  const [importProvider, setImportProvider] = useState<number | ''>('');
  const [importProcedure, setImportProcedure] = useState<number | ''>('');
  const [importOperatory, setImportOperatory] = useState<number | ''>('');
  // '' = Hauptkalender, sonst Kalender-ID eines read-only Zusatzkalenders
  const [importSource, setImportSource] = useState<string>('');
  const [extraCalendars, setExtraCalendars] = useState<string[]>([]);
  const [importBusy, setImportBusy] = useState(false);

  useEffect(() => {
    loadDashboardData();
    // Patienten-Aktionen unabhängig laden — Fehler hier dürfen das
    // restliche Dashboard nicht blockieren.
    // Inkl. erledigter laden — clientseitig nach offen/erledigt getrennt, damit
    // der Toggle ohne Nachladen funktioniert.
    apiService.getPatientActions(7, true)
      .then(resp => { if (resp.data?.success) setPatientActions(resp.data.data); })
      .catch(() => { /* nicht kritisch */ });
    // Fehlgeschlagene Mails (Mini-CRM) — nur mit Capability, sonst 403.
    if (can('communications')) {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      apiService.getCommunicationsFailedCount(since)
        .then(resp => { if (resp.data?.success) setFailedMail(resp.data.data); })
        .catch(() => { /* nicht kritisch */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Eine Self-Service-Aktion als erledigt markieren (optimistisch).
  const acknowledgeAction = async (a: PatientAction) => {
    const stamp = new Date().toISOString();
    setPatientActions(prev => prev.map(x =>
      x.id === a.id ? { ...x, acknowledged_at: stamp, acknowledged_by: user?.username || 'admin' } : x));
    try {
      await apiService.acknowledgePatientAction(a.id);
      addToast(t('dashboard.patientActionAcked', { name: a.patient_name }), 'success');
    } catch {
      // Rollback bei Fehler
      setPatientActions(prev => prev.map(x =>
        x.id === a.id ? { ...x, acknowledged_at: null, acknowledged_by: null } : x));
      addToast(t('common.errorGeneric'), 'error');
    }
  };

  // Quittung zurücknehmen (Undo).
  const undoAcknowledgeAction = async (a: PatientAction) => {
    setPatientActions(prev => prev.map(x =>
      x.id === a.id ? { ...x, acknowledged_at: null, acknowledged_by: null } : x));
    try {
      await apiService.unacknowledgePatientAction(a.id);
    } catch {
      setPatientActions(prev => prev.map(x =>
        x.id === a.id ? { ...x, acknowledged_at: a.acknowledged_at, acknowledged_by: a.acknowledged_by } : x));
      addToast(t('common.errorGeneric'), 'error');
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const today = new Date().toISOString().split('T')[0];
      const now = new Date();
      // Nur ab heute: Vergangene Google-only-Events sind kein Handlungsbedarf
      // (Auto-Import existiert) und sollen Banner/Statistiken nicht aufblähen.
      const from = moment().startOf('day').toISOString();
      const to = moment().add(30, 'days').toISOString();

      const [upcomingResp, allResp, gevResp] = await Promise.allSettled([
        apiService.getUpcomingAppointments(),
        apiService.getAppointments(),
        apiService.listGoogleEvents(from, to),
      ]);

      const upcoming = upcomingResp.status === 'fulfilled' ? (upcomingResp.value.data.data || []) : [];
      const all = (allResp.status === 'fulfilled' ? (allResp.value.data.data || []) : []) as Appointment[];
      const gevs = (gevResp.status === 'fulfilled' ? (gevResp.value.data.data || []) : []) as GoogleCalendarEvent[];

      setPendingWebsite(
        all
          .filter(a => a.status === 'pending' && a.source === 'website')
          .sort((a, b) =>
            `${a.appointment_date} ${a.start_time}`.localeCompare(
              `${b.appointment_date} ${b.start_time}`
            )
          )
      );

      // Dedupe: Google events that already correspond to an app-appointment are app-tracked.
      const apptEventIds = new Set(all.map(a => a.google_calendar_event_id).filter(Boolean) as string[]);
      const apptIds = new Set(all.map(a => String(a.id)));
      const googleOnly = gevs
        .filter(g => !apptEventIds.has(g.id))
        .filter(g => !g.appointmentId || !apptIds.has(g.appointmentId));
      setGoogleOnlyCount(googleOnly.length);
      setGoogleOnlyEvents(
        [...googleOnly].sort((a, b) =>
          (a.start?.dateTime || a.start?.date || '').localeCompare(b.start?.dateTime || b.start?.date || '')
        )
      );

      // Build merged upcoming list (next 7 days). Sort by start datetime ascending.
      const horizon = new Date(Date.now() + 7 * 86400000);
      const upcomingApptItems: UpcomingItem[] = upcoming.map((a: Appointment) => ({
        kind: 'appt' as const,
        sortKey: `${a.appointment_date}T${a.start_time}`,
        appt: a,
      }));
      const upcomingGoogleItems: UpcomingItem[] = googleOnly
        .filter(g => g.status !== 'cancelled')
        .filter(g => g.start?.dateTime || g.start?.date)
        .map(g => {
          const startSrc = g.start!.dateTime || g.start!.date!;
          const d = new Date(startSrc);
          return {
            kind: 'google' as const,
            sortKey: startSrc,
            gevent: g,
            date: moment(d).format('YYYY-MM-DD'),
            time: g.start?.dateTime ? moment(d).format('HH:mm') : '',
          };
        })
        .filter(it => {
          const d = new Date(it.sortKey);
          return d >= new Date() && d <= horizon;
        });
      const merged = [...upcomingApptItems, ...upcomingGoogleItems]
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
      setUpcomingMerged(merged);

      const todaysAppointments = upcoming.filter((apt: Appointment) => apt.appointment_date === today).length;
      const completedAppointments = upcoming.filter((apt: Appointment) => apt.status === 'completed').length;
      const pendingConfirmations = all.filter((apt: Appointment) => apt.status === 'pending').length;
      const noShows = upcoming.filter((apt: Appointment) => apt.status === 'no_show').length;

      // Google-only contribution
      const todayStart = new Date(`${today}T00:00:00`);
      const todayEnd = new Date(`${today}T23:59:59`);
      const todaysGoogleOnly = googleOnly.filter(g => {
        if (g.status === 'cancelled') return false;
        const startSrc = g.start?.dateTime || g.start?.date;
        if (!startSrc) return false;
        const d = new Date(startSrc);
        return d >= todayStart && d <= todayEnd;
      }).length;
      const completedGoogleOnly = googleOnly.filter(g => {
        if (g.status === 'cancelled') return false;
        const endSrc = g.end?.dateTime || g.end?.date;
        if (!endSrc) return false;
        return new Date(endSrc) < now;
      }).length;

      setStats({
        todaysAppointments: todaysAppointments + todaysGoogleOnly,
        todaysGoogleOnly,
        completedAppointments: completedAppointments + completedGoogleOnly,
        completedGoogleOnly,
        pendingConfirmations,
        noShows,
      });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(t('dashboard.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const openImportModal = async () => {
    setShowImport(true);
    if (importProviders.length === 0) {
      try {
        const [p, pr, o, gc] = await Promise.all([
          apiService.getProviders(true),
          apiService.getProcedures(true),
          apiService.getOperatories(true),
          apiService.get<any>('/settings/google-calendar').catch(() => null),
        ]);
        setImportProviders(p.data.data || []);
        setImportProcedures(pr.data.data || []);
        setImportOperatories(o.data.data || []);
        setExtraCalendars(gc?.data?.data?.extraCalendarIds || []);
      } catch (e: any) {
        addToast(e?.response?.data?.message || t('dashboard.loadMastersError'), 'error');
      }
    }
  };

  const handleBulkImport = async () => {
    if (!importProvider || !importProcedure || !importOperatory) {
      addToast(t('dashboard.selectAllError'), 'error');
      return;
    }
    setImportBusy(true);
    try {
      const resp = await apiService.bulkConvertGoogleEvents({
        provider_id: Number(importProvider),
        procedure_id: Number(importProcedure),
        operatory_id: Number(importOperatory),
        source_calendar_id: importSource || undefined,
      });
      if (resp.data?.success) {
        const d = resp.data.data;
        const errorsSuffix = d.errors.length ? t('dashboard.importErrorsSuffix', { count: d.errors.length }) : '';
        addToast(t('dashboard.importToast', { converted: d.converted, skipped: d.skipped, errorsSuffix }), d.errors.length ? 'info' : 'success');
        if (d.errors.length) {
          console.warn('Bulk import errors:', d.errors);
        }
        setShowImport(false);
        await loadDashboardData();
      } else {
        addToast(resp.data?.message || t('dashboard.importFailed'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('dashboard.importFailed'), 'error');
    } finally {
      setImportBusy(false);
    }
  };

  const formatDate = (dateString: string) =>
    fmtLocaleDate(dateString, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });

  const formatTime = (timeString: string) =>
    fmtLocaleDateTime(`2000-01-01T${timeString}`, {
      hour: '2-digit',
      minute: '2-digit',
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-border border-t-brand-primary"></div>
      </div>
    );
  }

  const todayLong = fmtLocaleDate(new Date(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-2xl shadow-md ring-1 ring-black/5 group">
        <img
          src="/images/admin-header-palma.webp"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-center scale-105 group-hover:scale-[1.08] transition-transform duration-[1500ms] ease-out"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-brand-primary/95 via-brand-primary/70 to-brand-primary/15"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
        <div
          className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-brand-accent/20 blur-3xl"
          aria-hidden
        />

        <div className="relative px-6 py-7 sm:px-8 sm:py-9">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block h-px w-8 bg-brand-accent" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-accent">
              {t('dashboard.eyebrow')}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-white leading-tight tracking-tight">
            {t(getGreetingKey())}{user?.firstName ? `, ${user.firstName}` : ''}
          </h1>
          <p className="text-sm text-white/85 mt-1.5 max-w-md">
            <Trans
              i18nKey="dashboard.todayIntro"
              values={{ date: todayLong }}
              components={{ 1: <span className="font-medium text-white" /> }}
            />
          </p>
          <Link
            to="/admin/quick-appointment"
            className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-white px-5 text-sm font-bold text-brand-primary shadow-md transition-colors duration-200 hover:bg-white/90 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <Zap className="h-5 w-5" strokeWidth={2.25} aria-hidden />
            {t('quick.title')}
          </Link>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3">
          <p className="text-sm font-medium text-rose-800 dark:text-rose-300">{error}</p>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <StatCard
          icon={CalendarIcon}
          label={t('dashboard.statTodaysAppointments')}
          value={stats.todaysAppointments}
          tone="primary"
          subtitle={stats.todaysGoogleOnly > 0 ? t('dashboard.googleOnlySubtitle', { count: stats.todaysGoogleOnly }) : undefined}
        />
        <StatCard
          icon={CalendarCheck}
          label={t('dashboard.statCompleted')}
          value={stats.completedAppointments}
          tone="success"
          subtitle={stats.completedGoogleOnly > 0 ? t('dashboard.googleOnlySubtitle', { count: stats.completedGoogleOnly }) : undefined}
        />
        <StatCard
          icon={CalendarClock}
          label={t('dashboard.statPendingConfirmations')}
          value={stats.pendingConfirmations}
          tone="warning"
        />
        <StatCard
          icon={CalendarX}
          label={t('dashboard.statNoShows')}
          value={stats.noShows}
          tone="danger"
        />
      </div>

      {/* Mini-CRM: fehlgeschlagene Mails (7 Tage) — Klick führt zur Akte
          (genau ein Patient) bzw. zur Patientenliste. */}
      {can('communications') && failedMail && failedMail.count > 0 && (
        <button
          type="button"
          onClick={() =>
            navigate(
              failedMail.patient_ids.length === 1
                ? `/admin/patients/${failedMail.patient_ids[0]}`
                : '/admin/patients'
            )
          }
          className="w-full flex items-center gap-2.5 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-left hover:bg-rose-100 dark:hover:bg-rose-500/15 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/40"
        >
          <MailWarning className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" strokeWidth={1.75} aria-hidden />
          <span className="text-sm font-medium text-rose-800 dark:text-rose-300">
            {t('dashboard.failedMails', { count: failedMail.count })}
          </span>
          <span className="hidden sm:inline text-xs text-rose-700/70 dark:text-rose-300/70">
            {t('dashboard.failedMailsHint')}
          </span>
          <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" strokeWidth={2} aria-hidden />
        </button>
      )}

      {/* Google-only events banner with bulk-import action */}
      {googleOnlyCount > 0 && (
        <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 sm:flex-1">
              <h3 className="text-sm font-semibold text-brand-text">{t('dashboard.googleBannerTitle')}</h3>
              <p className="text-xs text-brand-text-muted mt-0.5">
                <Trans
                  i18nKey="dashboard.googleBannerText"
                  values={{ count: googleOnlyCount }}
                  components={{ 1: <span className="font-semibold text-brand-text tabular-nums" /> }}
                />
              </p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={() => setShowGoogleList(v => !v)}
                className="inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-brand-text bg-brand-surface border border-brand-border hover:bg-brand-border/30 shadow-sm transition-colors"
              >
                {showGoogleList ? t('dashboard.googleBannerHide') : t('dashboard.googleBannerShow')}
              </button>
              <button
                onClick={openImportModal}
                className="inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors"
              >
                {t('dashboard.importAll')}
              </button>
            </div>
          </div>
          {showGoogleList && (
            <ul className="mt-3 pt-1 border-t border-brand-border divide-y divide-brand-border max-h-80 overflow-y-auto">
              {googleOnlyEvents.map(g => {
                const startSrc = g.start?.dateTime || g.start?.date;
                const isAllDay = !g.start?.dateTime;
                return (
                  <li
                    key={`${g.sourceCalendarId || 'main'}:${g.id}`}
                    className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                  >
                    <span className="shrink-0 tabular-nums text-brand-text-muted">
                      {startSrc
                        ? fmtLocaleDate(startSrc, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '—'}
                    </span>
                    <span className="shrink-0 tabular-nums text-brand-text-muted">
                      {isAllDay ? t('dashboard.googleBannerAllDay') : moment(startSrc).format('HH:mm')}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium text-brand-text">
                      {g.summary || t('dashboard.googleBannerNoTitle')}
                    </span>
                    {g.sourceCalendarId && (
                      <StatusPill tone="neutral">{g.sourceCalendarId}</StatusPill>
                    )}
                    {g.status === 'cancelled' && (
                      <StatusPill tone="danger">{t('dashboard.statusCancelled')}</StatusPill>
                    )}
                    {g.htmlLink && (
                      <a
                        href={g.htmlLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-xs font-medium text-brand-primary hover:underline"
                      >
                        {t('dashboard.googleBannerOpenInGoogle')}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* Self-Service-Aktionen von Patienten (Absagen/Umbuchungen) */}
      {patientActions.length > 0 && (() => {
        const openActions = patientActions.filter(a => !a.acknowledged_at);
        const ackedActions = patientActions.filter(a => a.acknowledged_at);
        const canAck = can('appointments');
        const renderRow = (a: PatientAction, acked: boolean) => (
          <li key={a.id} className={`py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm ${acked ? 'opacity-60' : ''}`}>
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${a.action === 'cancelled' ? 'bg-rose-100 text-rose-800' : 'bg-blue-100 text-blue-800'}`}>
              {a.action === 'cancelled' ? t('dashboard.patientActionCancelled') : t('dashboard.patientActionRescheduled')}
            </span>
            <Link to={`/admin/appointments`} className="font-medium text-brand-text hover:text-brand-primary transition-colors">{a.patient_name}</Link>
            <span className="text-brand-text-muted">{a.procedure_name}</span>
            <span className="text-brand-text-muted tabular-nums">
              {a.action === 'rescheduled' && a.previous_date
                ? `${a.previous_date} ${a.previous_time || ''} → ${a.appointment_date} ${a.start_time || ''}`
                : `${a.appointment_date || ''} ${a.start_time || ''}`}
            </span>
            <span className="ml-auto text-xs text-brand-text-muted tabular-nums">
              {new Date(a.changed_at).toLocaleString(undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
            {acked ? (
              <span className="inline-flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                  <Check className="h-3.5 w-3.5" strokeWidth={2} /> {t('dashboard.patientActionAckedBadge')}
                </span>
                {canAck && (
                  <button onClick={() => undoAcknowledgeAction(a)} className="text-xs font-medium text-brand-primary hover:underline cursor-pointer">
                    {t('dashboard.patientActionUndo')}
                  </button>
                )}
              </span>
            ) : (
              canAck && (
                <button
                  onClick={() => acknowledgeAction(a)}
                  title={t('dashboard.patientActionAckTitle')}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded transition-colors cursor-pointer"
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={2} /> {t('dashboard.patientActionAck')}
                </button>
              )
            )}
          </li>
        );
        return (
          <section className="rounded-xl bg-brand-surface border border-amber-300 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-brand-text flex items-center gap-2">
              <span aria-hidden>🔔</span>
              {openActions.length > 0
                ? t('dashboard.patientActionsTitle', { count: openActions.length })
                : t('dashboard.patientActionsAllDone')}
            </h3>
            {openActions.length > 0 && (
              <ul className="mt-3 divide-y divide-brand-border">
                {openActions.map(a => renderRow(a, false))}
              </ul>
            )}
            {showAckedActions && ackedActions.length > 0 && (
              <ul className="mt-2 divide-y divide-brand-border border-t border-brand-border pt-2">
                {ackedActions.map(a => renderRow(a, true))}
              </ul>
            )}
            {ackedActions.length > 0 && (
              <div className="mt-3 text-right">
                <button
                  onClick={() => setShowAckedActions(v => !v)}
                  className="text-xs font-medium text-brand-primary hover:underline cursor-pointer"
                >
                  {showAckedActions
                    ? t('dashboard.patientActionsHideAcked')
                    : t('dashboard.patientActionsShowAcked', { count: ackedActions.length })}
                </button>
              </div>
            )}
          </section>
        );
      })()}

      {/* Anamnese-QR-Overlay: vollflächig und opak — der Patient scannt am
          Empfangsbildschirm, daher dürfen keine anderen Patientendaten
          sichtbar oder lesbar sein (Datenschutz). */}
      {qrAppointment && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('dashboard.qrTitle')}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-bg/95 backdrop-blur-2xl p-4 overflow-y-auto"
          onClick={() => setQrAppointment(null)}
        >
          <button
            onClick={() => setQrAppointment(null)}
            aria-label={t('dashboard.qrClose')}
            className="absolute top-4 right-4 flex h-11 w-11 items-center justify-center rounded-full text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 transition-colors cursor-pointer"
          >
            <CloseIcon className="h-5 w-5" strokeWidth={2} aria-hidden />
          </button>
          <div
            className="flex flex-col items-center text-center max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-primary">
              SeaDent Mallorca
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-brand-text">
              {t('dashboard.qrTitle')}
            </h2>
            <p className="mt-1 text-sm text-brand-text-muted">
              {qrAppointment.patient_name} · {formatDate(qrAppointment.appointment_date)}, {formatTime(qrAppointment.start_time)}
            </p>
            <div className="mt-8 rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/5">
              <QRCodeSVG
                value={`${PUBLIC_SITE_BASE}/intake/${qrAppointment.intake_token}`}
                size={256}
                level="M"
              />
            </div>
            <p className="mt-8 max-w-[38ch] text-base leading-relaxed text-brand-text">
              {t('dashboard.qrInstruction')}
            </p>
            <button
              autoFocus
              onClick={() => setQrAppointment(null)}
              className="mt-10 inline-flex items-center justify-center px-8 py-3 text-sm font-semibold rounded-lg text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 transition-colors cursor-pointer"
            >
              {t('dashboard.qrClose')}
            </button>
          </div>
        </div>
      )}

      {/* Bulk-Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !importBusy && setShowImport(false)}>
          <div className="bg-brand-surface rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-brand-border">
              <h2 className="text-lg font-bold text-brand-text">{t('dashboard.importModalTitle')}</h2>
              <p className="text-xs text-brand-text-muted mt-1">
                <Trans
                  i18nKey="dashboard.importModalText"
                  values={{ count: googleOnlyCount }}
                  components={{ 1: <span className="font-semibold" /> }}
                />
              </p>
            </div>
            <div className="p-6 space-y-3">
              {extraCalendars.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">{t('dashboard.importSource')}</label>
                  <select value={importSource} onChange={(e) => setImportSource(e.target.value)} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                    <option value="">{t('dashboard.importSourceMain')}</option>
                    {extraCalendars.map(id => <option key={id} value={id}>{t('dashboard.importSourceExtra', { id })}</option>)}
                  </select>
                  {importSource && (
                    <p className="mt-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2">
                      {t('dashboard.importSourceExtraHint')}
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">{t('dashboard.defaultProvider')}</label>
                <select value={importProvider} onChange={(e) => setImportProvider(e.target.value ? Number(e.target.value) : '')} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">{t('dashboard.selectProvider')}</option>
                  {importProviders.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">{t('dashboard.defaultProcedure')}</label>
                <select value={importProcedure} onChange={(e) => setImportProcedure(e.target.value ? Number(e.target.value) : '')} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">{t('dashboard.selectProcedure')}</option>
                  {importProcedures.map(p => {
                    const name = typeof p.name === 'string' ? p.name : (p.name as any).de || (p.name as any).en || '?';
                    return <option key={p.id} value={p.id}>{name}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">{t('dashboard.defaultOperatory')}</label>
                <select value={importOperatory} onChange={(e) => setImportOperatory(e.target.value ? Number(e.target.value) : '')} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">{t('dashboard.selectOperatory')}</option>
                  {importOperatories.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                {t('dashboard.importWarning')}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-brand-border flex justify-end gap-2">
              <button onClick={() => setShowImport(false)} disabled={importBusy} className="px-3 py-1.5 text-sm border border-brand-border rounded text-brand-text hover:bg-brand-surface-2 disabled:opacity-50">{t('common.cancel')}</button>
              <button onClick={handleBulkImport} disabled={importBusy} className="px-4 py-1.5 text-sm bg-brand-primary text-white rounded font-semibold disabled:opacity-50">
                {importBusy ? t('dashboard.importing') : t('dashboard.importNow')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending website bookings */}
      {pendingWebsite.length > 0 && (
        <section className="rounded-xl bg-brand-surface border border-amber-200 dark:border-amber-500/30 shadow-sm overflow-hidden">
          <header className="flex items-center justify-between gap-3 px-6 py-4 bg-amber-50/60 dark:bg-amber-500/5 border-b border-amber-200/60 dark:border-amber-500/20">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400">
                <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
                <span
                  aria-hidden
                  className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-brand-surface animate-pulse"
                />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-brand-text">
                  {t('dashboard.pendingTitle')}
                </h2>
                <p className="text-xs text-brand-text-muted">
                  {t('dashboard.pendingSubtitle', { count: pendingWebsite.length })}
                </p>
              </div>
            </div>
            <StatusPill tone="warning" dot>
              {pendingWebsite.length}
            </StatusPill>
          </header>

          <ul className="divide-y divide-brand-border">
            {pendingWebsite.map(a => (
              <li key={a.id}>
                <Link
                  to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                  className="block px-6 py-4 hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:bg-brand-surface-2"
                  title={t('dashboard.openInCalendar')}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-brand-text">
                          {a.patient_name}
                        </span>
                        <StatusPill tone="warning">{t('dashboard.website')}</StatusPill>
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-brand-text-muted">
                        <CalendarIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                        <span>
                          {formatDate(a.appointment_date)} · {formatTime(a.start_time)}–{formatTime(a.end_time)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-brand-text-muted flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Smile className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.procedure_name}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Stethoscope className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.provider_name}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.operatory_name}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-brand-text-muted flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.patient_email}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.patient_phone}
                        </span>
                      </div>
                      {a.notes && (
                        <div className="mt-2 text-xs text-brand-text whitespace-pre-line bg-brand-surface-2 px-3 py-2 rounded-md border border-brand-border">
                          {a.notes}
                        </div>
                      )}
                    </div>
                    <ChevronRight
                      className="h-4 w-4 text-brand-text-muted shrink-0 mt-1"
                      strokeWidth={2}
                      aria-hidden
                    />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Upcoming appointments (merged: app + google-only) */}
      <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
        <header className="px-6 py-4 border-b border-brand-border">
          <h2 className="text-sm font-semibold text-brand-text">{t('dashboard.upcomingTitle')}</h2>
          <p className="text-xs text-brand-text-muted mt-0.5">{t('dashboard.upcomingSubtitle')}</p>
        </header>
        <ul className="divide-y divide-brand-border">
          {upcomingMerged.length === 0 ? (
            <li className="px-6 py-10 text-center text-sm text-brand-text-muted">
              {t('dashboard.noUpcoming')}
            </li>
          ) : (
            upcomingMerged.slice(0, 10).map(item => {
              if (item.kind === 'appt') {
                const appointment = item.appt;
                const meta = STATUS_META[appointment.status] ?? { labelKey: '', tone: 'neutral' as StatusTone };
                const metaLabel = meta.labelKey ? t(meta.labelKey) : appointment.status;
                return (
                  <li key={`appt-${appointment.id}`} className="flex items-stretch hover:bg-brand-surface-2 transition-colors">
                    <Link
                      to={`/admin/calendar?date=${appointment.appointment_date}&id=${appointment.id}`}
                      className="block flex-1 min-w-0 pl-6 pr-2 py-3.5 cursor-pointer focus:outline-none focus-visible:bg-brand-surface-2"
                      title={t('dashboard.openInCalendar')}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-brand-primary text-sm font-semibold">
                            {appointment.patient_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-brand-text truncate">
                              {appointment.patient_name}
                            </p>
                            <p className="text-xs text-brand-text-muted">
                              {formatDate(appointment.appointment_date)} · {formatTime(appointment.start_time)}
                            </p>
                          </div>
                        </div>
                        <div className="hidden sm:flex flex-col items-end leading-tight text-xs text-brand-text-muted min-w-0">
                          <span className="truncate max-w-[180px]">{appointment.provider_name}</span>
                          <span className="truncate max-w-[180px]">{appointment.procedure_name}</span>
                        </div>
                        <StatusPill tone={meta.tone} dot>{metaLabel}</StatusPill>
                      </div>
                    </Link>
                    {/* Quick actions: Anamnese-QR + Rechnung — bewusst außerhalb des Links */}
                    <div className="flex items-center gap-1 pr-4 sm:pr-5">
                      {appointment.intake_token && (
                        <button
                          onClick={() => setQrAppointment(appointment)}
                          title={t('dashboard.qrAction')}
                          aria-label={t('dashboard.qrAction')}
                          className="flex h-9 w-9 items-center justify-center rounded-md text-brand-text-muted hover:text-brand-primary hover:bg-brand-accent/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 transition-colors cursor-pointer"
                        >
                          <QrCode className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
                        </button>
                      )}
                      {can('invoices-manage') && appointment.patient_id && (
                        <button
                          onClick={() => navigate(`/admin/invoices/new?patient=${appointment.patient_id}&appointment=${appointment.id}`)}
                          title={t('dashboard.invoiceAction')}
                          aria-label={t('dashboard.invoiceAction')}
                          className="flex h-9 w-9 items-center justify-center rounded-md text-brand-text-muted hover:text-brand-primary hover:bg-brand-accent/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 transition-colors cursor-pointer"
                        >
                          <Receipt className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
                        </button>
                      )}
                    </div>
                  </li>
                );
              }
              // google-only event
              const g = item.gevent;
              const title = g.summary || t('dashboard.noTitle');
              return (
                <li key={`gcal-${g.id}`}>
                  <Link
                    to={`/admin/calendar?date=${item.date}`}
                    className="block px-6 py-3.5 hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:bg-brand-surface-2"
                    title={t('dashboard.openInCalendarGoogle')}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-600 text-sm font-semibold border border-dotted border-gray-400" title={t('dashboard.onlyInGoogle')}>
                          G
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-brand-text truncate">
                            {title}
                          </p>
                          <p className="text-xs text-brand-text-muted">
                            {formatDate(item.date)}{item.time ? ` · ${item.time}` : ` · ${t('dashboard.allDay')}`}
                          </p>
                        </div>
                      </div>
                      <div className="hidden sm:flex flex-col items-end leading-tight text-xs text-brand-text-muted min-w-0">
                        {g.location && <span className="truncate max-w-[180px]">{g.location}</span>}
                      </div>
                      <StatusPill tone="neutral">{t('dashboard.google')}</StatusPill>
                    </div>
                  </Link>
                </li>
              );
            })
          )}
        </ul>
      </section>

      {/* Quick actions */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-3">
          {t('dashboard.quickAccess')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <QuickAction to="/admin/calendar" icon={CalendarIcon} title={t('dashboard.quickCalendar')} desc={t('dashboard.quickCalendarDesc')} />
          <QuickAction to="/admin/appointments" icon={ClipboardList} title={t('dashboard.quickAppointments')} desc={t('dashboard.quickAppointmentsDesc')} />
          <QuickAction to="/admin/providers" icon={Stethoscope} title={t('dashboard.quickProviders')} desc={t('dashboard.quickProvidersDesc')} />
        </div>
      </section>
    </div>
  );
};

const QuickAction: React.FC<{
  to: string;
  icon: LucideIcon;
  title: string;
  desc: string;
}> = ({ to, icon: Icon, title, desc }) => (
  <Link
    to={to}
    className="group flex items-center gap-3 rounded-xl bg-brand-surface border border-brand-border p-4 hover:border-brand-accent/60 hover:shadow-sm transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-accent/10 text-brand-primary group-hover:bg-brand-accent/20 transition-colors">
      <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-brand-text">{title}</p>
      <p className="text-xs text-brand-text-muted">{desc}</p>
    </div>
    <ChevronRight
      className="h-4 w-4 text-brand-text-muted shrink-0 group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all"
      strokeWidth={2}
      aria-hidden
    />
  </Link>
);

export default Dashboard;
